'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useStyling } from '@/store/CedarStore';

interface SelectProps {
	value?: string;
	onValueChange?: (value: string) => void;
	placeholder?: string;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
}

interface SelectTriggerProps {
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
}

interface SelectContentProps {
	children: React.ReactNode;
	className?: string;
}

interface SelectItemProps {
	value: string;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
}

const SelectContext = React.createContext<{
	value?: string;
	onValueChange?: (value: string) => void;
	open: boolean;
	setOpen: (open: boolean) => void;
	searchQuery: string;
	setSearchQuery: (query: string) => void;
}>({
	open: false,
	setOpen: () => {},
	searchQuery: '',
	setSearchQuery: () => {},
});

const Select = ({ value, onValueChange, children, className }: SelectProps) => {
	const [open, setOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const selectRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				selectRef.current &&
				!selectRef.current.contains(event.target as Node)
			) {
				setOpen(false);
				setSearchQuery('');
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<SelectContext.Provider
			value={{
				value,
				onValueChange,
				open,
				setOpen,
				searchQuery,
				setSearchQuery,
			}}>
			<div ref={selectRef} className={cn('relative', className)}>
				{children}
			</div>
		</SelectContext.Provider>
	);
};

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
	({ children, className, disabled }, ref) => {
		const { open, setOpen } = React.useContext(SelectContext);

		return (
			<button
				ref={ref}
				type='button'
				role='combobox'
				aria-expanded={open}
				className={cn(
					'flex h-12 w-full items-center justify-between whitespace-nowrap rounded-md px-3 py-3 text-base shadow-sm transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
					className
				)}
				style={{
					backgroundColor: 'var(--input-background-color)',
					color: 'var(--text-color)',
				}}
				onClick={() => !disabled && setOpen(!open)}
				disabled={disabled}>
				{children}
				<ChevronDown
					className={cn(
						'h-4 w-4 shrink-0 opacity-50 transition-transform duration-200',
						open && 'rotate-180'
					)}
				/>
			</button>
		);
	}
);
SelectTrigger.displayName = 'SelectTrigger';

const SelectValue = React.forwardRef<
	HTMLSpanElement,
	{ children?: React.ReactNode }
>(({ children }, ref) => {
	const { value } = React.useContext(SelectContext);
	return (
		<span ref={ref} className='flex-1 truncate text-left'>
			{children || value}
		</span>
	);
});
SelectValue.displayName = 'SelectValue';

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
	({ children, className }, ref) => {
		const { open, searchQuery, setSearchQuery } =
			React.useContext(SelectContext);
		const searchInputRef = useRef<HTMLInputElement>(null);

		useEffect(() => {
			if (open && searchInputRef.current) {
				searchInputRef.current.focus();
			}
		}, [open]);

		if (!open) return null;

		const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchQuery(e.target.value);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Escape') {
				setSearchQuery('');
			}
		};

		return (
			<div
				ref={ref}
				className={cn(
					'max-h-[500px] absolute z-50 w-full min-w-[8rem] overflow-hidden rounded-md bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
					className
				)}
				style={{
					backgroundColor: 'var(--input-background-color)',
					color: 'var(--text-color)',
				}}>
				<div className='p-1'>
					<div className='px-2 py-1.5'>
						<input
							ref={searchInputRef}
							type='text'
							value={searchQuery}
							onChange={handleSearchChange}
							onKeyDown={handleKeyDown}
							placeholder='Search...'
							className='w-full rounded-md bg-transparent px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none'
							style={{
								color: 'var(--text-color)',
							}}
						/>
					</div>
					<div className='max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
						{React.Children.map(children, (child) => {
							if (!React.isValidElement(child)) return child;

							const itemText =
								child.props.children?.toString().toLowerCase() || '';
							const matchesSearch =
								searchQuery === '' ||
								itemText.includes(searchQuery.toLowerCase());

							return matchesSearch ? child : null;
						})}
					</div>
				</div>
			</div>
		);
	}
);
SelectContent.displayName = 'SelectContent';

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
	({ value, children, className, disabled }, ref) => {
		const {
			value: selectedValue,
			onValueChange,
			setOpen,
		} = React.useContext(SelectContext);
		const { styling } = useStyling();
		const isSelected = value === selectedValue;
		const primaryColor = styling.color || '#3B82F6';

		const handleClick = () => {
			if (!disabled) {
				onValueChange?.(value);
				setOpen(false);
			}
		};

		return (
			<div
				ref={ref}
				role='option'
				aria-selected={isSelected}
				className={cn(
					'pointer-cursor relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-2 pr-8 text-sm outline-none text-left hover:bg-[var(--hover-color)] transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
					disabled && 'cursor-not-allowed opacity-50',
					className
				)}
				style={
					{
						'--hover-color': `${primaryColor}30`,
					} as React.CSSProperties
				}
				onClick={handleClick}>
				<span className='flex-1 text-left'>{children}</span>
				{isSelected && (
					<span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
						<Check className='h-4 w-4' />
					</span>
				)}
			</div>
		);
	}
);
SelectItem.displayName = 'SelectItem';

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
